/**
 * Categories, income heads and tags (FR-CAT-01 to FR-CAT-08).
 *
 * **Nothing is seeded here, deliberately.** FR-CAT-05 requires the category list to be
 * drafted from the actual 637 imported rows and handed over for editing — "do not invent
 * one" is a hard constraint, not a preference. This module gives the screen its CRUD; the
 * evidence-based draft arrives during M4 and is edited before import runs at M5.
 */

import {
  collection,
  doc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import {
  CategoryError,
  slugifyName,
  validateMerge,
  validateParent,
  type Category,
  type CategoryKind,
} from '../core/categories';
import { auditUpdate, writeAudit } from './audit';
import { db } from './firebase';
import { trackWrite } from './syncState';

const CATEGORIES = 'categories';
const TAGS = 'tags';

function toCategory(id: string, data: Record<string, unknown>): Category {
  return {
    id,
    name: (data.name as string) ?? id,
    parentId: (data.parentId as string) ?? null,
    kind: (data.kind as CategoryKind) ?? 'expense',
    archived: data.archived === true,
    sortOrder: (data.sortOrder as number) ?? 0,
    transactionCount: (data.transactionCount as number) ?? 0,
  };
}

export function watchCategories(onChange: (categories: Category[]) => void): () => void {
  return onSnapshot(
    query(collection(db, CATEGORIES), orderBy('sortOrder')),
    (snapshot) => onChange(snapshot.docs.map((d) => toCategory(d.id, d.data()))),
    () => onChange([]),
  );
}

export async function createCategory(
  input: { name: string; kind: CategoryKind; parentId: string | null },
  existing: Category[],
  by: string,
): Promise<string> {
  const name = input.name.trim();
  if (!name) throw new CategoryError('A category needs a name.');

  validateParent(existing, input.parentId);

  const id = slugifyName(name);
  if (!id) throw new CategoryError('That name produces an empty id.');
  if (existing.some((c) => c.id === id)) {
    throw new CategoryError(`"${name}" already exists.`);
  }

  await trackWrite(
    setDoc(doc(db, CATEGORIES, id), {
      name,
      kind: input.kind,
      parentId: input.parentId,
      archived: false,
      sortOrder: existing.length,
      transactionCount: 0,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      enteredBy: by,
    }),
  );
  return id;
}

/**
 * Rename a category. Retroactive by construction — transactions store the slug id, so
 * one write relabels every historical row (FR-CAT-04).
 */
export async function renameCategory(
  category: Category,
  name: string,
  by: string,
): Promise<void> {
  const trimmed = name.trim();
  if (!trimmed) throw new CategoryError('A category needs a name.');
  if (trimmed === category.name) return;

  await trackWrite(
    updateDoc(doc(db, CATEGORIES, category.id), { name: trimmed, updatedAt: serverTimestamp() }),
  );
  await auditUpdate(
    CATEGORIES,
    category.id,
    { name: category.name },
    { name: trimmed },
    by,
  );
}

/** Archive, never delete (FR-CAT-03). The rules deny deletion outright. */
export async function setCategoryArchived(
  category: Category,
  archived: boolean,
  by: string,
): Promise<void> {
  await trackWrite(
    updateDoc(doc(db, CATEGORIES, category.id), { archived, updatedAt: serverTimestamp() }),
  );
  await auditUpdate(
    CATEGORIES,
    category.id,
    { archived: category.archived },
    { archived },
    by,
  );
}

/**
 * Merge one category into another (FR-CAT-04).
 *
 * Re-points every transaction, archives the source, and writes an audit entry per
 * changed transaction (FR-CAT-10) — not one for the batch, because the audit log records
 * what happened to each record.
 *
 * Returns the number of transactions moved so the caller can say so, and can warn that
 * rollups need recomputing: rollup buckets are keyed by category id, and this changes
 * which bucket past transactions belong to.
 */
export async function mergeCategories(
  sourceId: string,
  targetId: string,
  existing: Category[],
  by: string,
): Promise<number> {
  validateMerge(existing, sourceId, targetId);

  const affected = await getDocs(
    query(collection(db, 'transactions'), where('categoryId', '==', sourceId)),
  );

  // Chunked well inside Firestore's 500-operation batch limit, and small enough that a
  // partial failure leaves an obvious, re-runnable state.
  const CHUNK = 100;
  for (let i = 0; i < affected.docs.length; i += CHUNK) {
    const batch = writeBatch(db);
    for (const document of affected.docs.slice(i, i + CHUNK)) {
      batch.update(document.ref, { categoryId: targetId, updatedAt: serverTimestamp() });
    }
    await trackWrite(batch.commit());
  }

  for (const document of affected.docs) {
    await writeAudit({
      action: 'update',
      collection: 'transactions',
      documentId: document.id,
      changes: [{ field: 'categoryId', before: sourceId, after: targetId }],
      by,
      context: 'bulk-reclassify',
    });
  }

  const source = existing.find((c) => c.id === sourceId);
  if (source) await setCategoryArchived(source, true, by);

  return affected.size;
}

// ---------------------------------------------------------------------------
// Tags (FR-CAT-07)
// ---------------------------------------------------------------------------

export interface Tag {
  id: string;
  name: string;
  archived: boolean;
  usageCount: number;
}

export function watchTags(onChange: (tags: Tag[]) => void): () => void {
  return onSnapshot(
    collection(db, TAGS),
    (snapshot) =>
      onChange(
        snapshot.docs
          .map((d) => ({
            id: d.id,
            name: (d.data().name as string) ?? d.id,
            archived: d.data().archived === true,
            usageCount: (d.data().usageCount as number) ?? 0,
          }))
          .sort((a, b) => a.name.localeCompare(b.name)),
      ),
    () => onChange([]),
  );
}

export async function createTag(name: string, existing: Tag[], by: string): Promise<string> {
  const trimmed = name.trim();
  const id = slugifyName(trimmed);
  if (!id) throw new CategoryError('That tag name produces an empty id.');
  if (existing.some((t) => t.id === id)) throw new CategoryError(`"${trimmed}" already exists.`);

  await trackWrite(
    setDoc(doc(db, TAGS, id), {
      name: trimmed,
      archived: false,
      usageCount: 0,
      createdAt: serverTimestamp(),
      enteredBy: by,
    }),
  );
  return id;
}
